// Utility functions for chart overlays and price lines

export interface OHLCData {
  t: number;      // timestamp
  o: number;      // open
  h: number;      // high
  l: number;      // low
  c: number;      // close
  v?: number;     // volume
}

// 🌟 คำนวณ "ช่วงแท่งเทียนที่ควรแสดงเป็นค่าเริ่มต้น" แบบนับจำนวนแท่ง (index-based)
// ใช้ร่วมกันทั้ง StockChart / RSIChart / MACDChart เพื่อให้ทุกกราฟ Sync ช่วงที่แสดงตรงกันเป๊ะ
// ตั้งแต่แรกที่สร้างกราฟ ไม่ต้องรอ event ซิงค์ (ซึ่งมีจังหวะ race กันได้ตอนสร้างกราฟพร้อมกันหลายตัว)
// หมายเหตุ: ไม่ใช้การเทียบวันที่ (new Date(d.t) >= fromDate) เพราะพังง่ายเมื่อแท่งห่างกันไม่เท่ากัน
// (เช่นรายสัปดาห์ห่างกันทีละ 7 วัน) ทำให้หา index ผิดพลาด
export function getDefaultVisibleLogicalRange(
  dataLength: number,
  zoomScope: string,
  frequency: string
): { from: number; to: number } | null {
  if (dataLength <= 0) return null;

  // จำนวนวันโดยประมาณของแต่ละช่วง zoomScope
  const daysInScopeMap: Record<string, number> = {
    '1D': 1,
    '5D': 5,
    '1M': 30,
    '6M': 182,
    'YTD': 365,
    '1Y': 365,
    '5Y': 365 * 5,
  };
  const totalDays = daysInScopeMap[zoomScope] ?? 182;

  // จำนวนวันโดยประมาณต่อ 1 แท่งเทียน ตาม frequency
  const daysPerBar =
    frequency === 'week' ? 7 :
      frequency === 'month' ? 30 :
        frequency === 'minute' ? (1 / 390) : // ~390 นาทีเทรดต่อวัน (6.5 ชม.)
          1; // day (default)

  // จำนวนแท่งเทียนที่ควรแสดง (ขั้นต่ำ 10 แท่ง กันแท่งเทียนล้นจอตอนข้อมูลน้อย)
  const desiredBarCount = Math.max(10, Math.round(totalDays / daysPerBar));
  const toIdx = dataLength - 1;
  const fromIdx = Math.max(0, toIdx - desiredBarCount + 1);

  return { from: fromIdx, to: toIdx };
}

export function calculateEMA(data: OHLCData[], period: number): { time: number; value: number }[] {
  if (!data || data.length === 0) return [];
  const emaData = [];
  const multiplier = 2 / (period + 1);

  // 🌟 เดิม: รอสะสมให้ครบ `period` แท่งก่อน (seed ด้วย SMA ของ period แท่งแรก) ถึงจะเริ่มมีค่า -
  // ถ้าข้อมูลมีจำกัด (เช่น 261 แท่งรายสัปดาห์ กับ period=200) เส้นจะโผล่มาแค่ท้ายกราฟสั้นๆ (~61 แท่ง)
  // ผิดจากที่แพลตฟอร์มชาร์ตทั่วไป (รวม TradingView) แสดง ซึ่งเห็นเส้นตลอดทั้งกราฟตั้งแต่แท่งแรกที่มีข้อมูล
  // จึง seed ด้วยราคาปิดของแท่งแรกแทน (เหมือนกับ ema() helper ภายใน calculateMACD ด้านล่างในไฟล์นี้)
  // แล้ววนสูตร EMA ตั้งแต่แท่งที่ 2 เป็นต้นไป ได้ค่าทุกแท่งตั้งแต่ต้นข้อมูล ไม่ต้องรอครบ period ก่อน
  // (ค่าช่วงแรกๆ จะยังไม่ใช่ค่าเฉลี่ยที่ "แม่น" เท่าตอนสะสมข้อมูลครบ period แท่งแล้ว แต่จะค่อยๆ ลู่เข้า
  // ค่าจริงเหมือน EMA ทั่วไป - นี่คือพฤติกรรมมาตรฐานของ EMA ที่ใช้กันทั่วไปในแพลตฟอร์มชาร์ต)
  let ema = data[0].c;
  emaData.push({
    time: Math.floor(data[0].t / 1000),
    value: ema
  });

  for (let i = 1; i < data.length; i++) {
    ema = (data[i].c - ema) * multiplier + ema;
    emaData.push({
      time: Math.floor(data[i].t / 1000),
      value: ema
    });
  }

  return emaData;
}

export function calculateBollingerBands(data: OHLCData[], period = 20, multiplier = 2) {
  if (!data || data.length < period) return [];
  const bands = [];
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    const closes = slice.map((d: OHLCData) => d.c);
    const mean = closes.reduce((a: number, b: number) => a + b, 0) / period;
    const variance = closes.reduce((a: number, b: number) => a + Math.pow(b - mean, 2), 0) / period;
    const std = Math.sqrt(variance);
    bands.push({
      time: Math.floor(data[i].t / 1000),
      upper: mean + multiplier * std,
      middle: mean,
      lower: mean - multiplier * std,
    });
  }
  return bands;
}

// Simple support/resistance: find local maxima/minima (for demo)
export function calculateSupportResistance(data: OHLCData[], lookback = 20) {
  if (!data || data.length < lookback) return { support: [], resistance: [] };
  const support = [], resistance = [];
  for (let i = lookback; i < data.length - lookback; i++) {
    const slice = data.slice(i - lookback, i + lookback + 1);
    const lows = slice.map((d: OHLCData) => d.l);
    const highs = slice.map((d: OHLCData) => d.h);
    if (data[i].l === Math.min(...lows)) {
      support.push({ time: Math.floor(data[i].t / 1000), value: data[i].l });
    }
    if (data[i].h === Math.max(...highs)) {
      resistance.push({ time: Math.floor(data[i].t / 1000), value: data[i].h });
    }
  }
  return { support, resistance };
}

// Calculate all EMAs for the chart - always return data even if limited
export function calculateAllEMAs(data: OHLCData[]) {
  return {
    ema50: calculateEMA(data, 50),
    ema100: calculateEMA(data, 100),
    ema200: calculateEMA(data, 200)
  };
}

export function calculateMACD(data: OHLCData[], fast = 12, slow = 26, signal = 9) {
  // Calculate EMA helper
  function ema(arr: number[], period: number): number[] {
    const k = 2 / (period + 1);
    let emaArr: number[] = [];
    let prevEma = arr[0];
    emaArr.push(prevEma);
    for (let i = 1; i < arr.length; i++) {
      prevEma = arr[i] * k + prevEma * (1 - k);
      emaArr.push(prevEma);
    }
    return emaArr;
  }
  const closes = data.map(d => d.c);
  const fastEma = ema(closes, fast);
  const slowEma = ema(closes, slow);
  const macdLine = fastEma.map((v, i) => v - slowEma[i]);
  const signalLine = ema(macdLine, signal);
  const histogram = macdLine.map((v, i) => v - signalLine[i]);
  return data.map((d, i) => ({
    time: Math.floor(d.t / 1000),
    macd: macdLine[i],
    signal: signalLine[i],
    hist: histogram[i]
  }));
}

export function calculateRSI(data: OHLCData[], period = 14) {
  const closes = data.map(d => d.c);
  // Emit one entry per input bar (padding the warm-up period as whitespace)
  // so downstream series stay index-aligned with the price/MACD series for X-axis sync.
  // 🌟 ใช้ Wilder's smoothing (มาตรฐานเดียวกับ Webull / TradingView) แทน SMA แบบหน้าต่างเลื่อน:
  // seed ด้วยค่าเฉลี่ยธรรมดาของ period แท่งแรก แล้วสะสมต่อด้วย avg = (prevAvg * (period - 1) + x) / period
  let rsis: { time: number, value?: number }[] = [];
  let avgGain = 0, avgLoss = 0;
  for (let i = 0; i < closes.length; i++) {
    const time = Math.floor(data[i].t / 1000);
    if (i === 0) {
      rsis.push({ time });
      continue;
    }
    const diff = closes[i] - closes[i - 1];
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    if (i <= period) {
      avgGain += gain / period;
      avgLoss += loss / period;
      if (i < period) {
        rsis.push({ time });
        continue;
      }
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
    }
    const rsi = avgLoss === 0 ? 100 : 100 - (100 / (1 + avgGain / avgLoss));
    rsis.push({ time, value: rsi });
  }
  return rsis;
}

